import { createStore, get, set, del } from 'idb-keyval';

import type { DocMetadata } from './types';
import { toDocSummary } from './metadata';

/** Per-document Yjs state and unpublished-update queues live in IndexedDB (can be large). */
const yjsStore = createStore('nostr-office-yjs', 'state');
/** Cached document metadata for instant dashboard paint. */
const metaStore = createStore('nostr-office-meta', 'metadata');

export function docKey(ownerPubkey: string, docId: string): string {
  return `${ownerPubkey}:${docId}`;
}

/** Persist the full Yjs state of a document. */
export function saveDocState(ownerPubkey: string, docId: string, state: Uint8Array): Promise<void> {
  return set(`state:${docKey(ownerPubkey, docId)}`, state, yjsStore);
}

/** Load the cached Yjs state of a document. */
export function loadDocState(ownerPubkey: string, docId: string): Promise<Uint8Array | undefined> {
  return get(`state:${docKey(ownerPubkey, docId)}`, yjsStore);
}

/** Persist the merged set of Yjs updates that have not been published yet. */
export function savePendingUpdates(ownerPubkey: string, docId: string, updates: Uint8Array): Promise<void> {
  return set(`pending:${docKey(ownerPubkey, docId)}`, updates, yjsStore);
}

export function loadPendingUpdates(ownerPubkey: string, docId: string): Promise<Uint8Array | undefined> {
  return get(`pending:${docKey(ownerPubkey, docId)}`, yjsStore);
}

export function clearPendingUpdates(ownerPubkey: string, docId: string): Promise<void> {
  return del(`pending:${docKey(ownerPubkey, docId)}`, yjsStore);
}

/** Cache parsed document metadata for offline / instant loads. */
export function saveCachedMetadata(metadata: DocMetadata): Promise<void> {
  return set(`meta:${docKey(metadata.ownerPubkey, metadata.docId)}`, metadata, metaStore);
}

export async function loadCachedMetadata(ownerPubkey: string, docId: string): Promise<DocMetadata | undefined> {
  return get(`meta:${docKey(ownerPubkey, docId)}`, metaStore);
}

export function deleteCachedDoc(ownerPubkey: string, docId: string): Promise<void> {
  const key = docKey(ownerPubkey, docId);
  return Promise.all([
    del(`state:${key}`, yjsStore),
    del(`pending:${key}`, yjsStore),
    del(`meta:${key}`, metaStore),
  ]).then(() => undefined);
}

/**
 * Local (per-browser) document records: favorites and last-opened
 * timestamps. Kept in localStorage since they are tiny.
 */

interface LocalDocPrefs {
  favorites: string[]; // doc keys ("owner:docId")
  recent: Record<string, number>; // doc key -> last opened at (ms)
}

const PREFS_PREFIX = 'nostr-office:prefs:';

function readPrefs(pubkey: string): LocalDocPrefs {
  try {
    const raw = localStorage.getItem(PREFS_PREFIX + pubkey);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LocalDocPrefs>;
      return {
        favorites: Array.isArray(parsed.favorites) ? parsed.favorites : [],
        recent: parsed.recent && typeof parsed.recent === 'object' ? parsed.recent : {},
      };
    }
  } catch {
    // fall through
  }
  return { favorites: [], recent: {} };
}

function writePrefs(pubkey: string, prefs: LocalDocPrefs): void {
  try {
    localStorage.setItem(PREFS_PREFIX + pubkey, JSON.stringify(prefs));
  } catch {
    // storage full / unavailable — non-critical
  }
}

export function getFavorites(pubkey: string): Set<string> {
  return new Set(readPrefs(pubkey).favorites);
}

export function toggleFavorite(pubkey: string, docKeyStr: string): boolean {
  const prefs = readPrefs(pubkey);
  const set = new Set(prefs.favorites);
  const wasFavorite = set.has(docKeyStr);
  if (wasFavorite) {
    set.delete(docKeyStr);
  } else {
    set.add(docKeyStr);
  }
  prefs.favorites = [...set];
  writePrefs(pubkey, prefs);
  return !wasFavorite;
}

export function touchRecent(pubkey: string, ownerPubkey: string, docId: string): void {
  const prefs = readPrefs(pubkey);
  prefs.recent[docKey(ownerPubkey, docId)] = Date.now();
  // Keep the map bounded.
  const entries = Object.entries(prefs.recent).sort((a, b) => b[1] - a[1]);
  if (entries.length > 200) {
    prefs.recent = Object.fromEntries(entries.slice(0, 200));
  }
  writePrefs(pubkey, prefs);
}

export function getRecentTimestamps(pubkey: string): Record<string, number> {
  return readPrefs(pubkey).recent;
}

export { toDocSummary };
